import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTACT_LIMITS } from '@shared/contact-schema';
import { useUrlLocale } from '../i18n/url-locale';
import { getContactContent } from '../lib/content';
import {
  fetchContactStatus,
  submitContact,
  validateContact,
  type ContactField,
  type ContactFormError,
  type ContactValues,
} from '../lib/contact-form';
import { usePageTitle } from '../lib/use-page-title';
import { useLanguageQuery } from '../components/site-link';

type Phase = 'ready' | 'sending' | 'sent' | 'disabled';

/**
 * The contact page. The form is real HTML that posts to /api/contact, so it
 * also works before (or without) JavaScript. Once hydrated it asks the Worker
 * whether delivery is configured; if not, the form is replaced by a plain
 * "not available yet" message.
 */
export function ContactPage() {
  const { t } = useTranslation();
  const locale = useUrlLocale();
  const page = getContactContent(locale);
  usePageTitle('/contact', page.meta.title);

  // An explicit ?lang= choice travels with a no-JavaScript post, so the
  // result page links back in the same language (empty until hydrated).
  const explicitLang = new URLSearchParams(useLanguageQuery()).get('lang');
  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<ContactFormError | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const resultRef = useRef<HTMLHeadingElement>(null);
  // Focus moves to the result only after the visitor submitted; the status
  // check on page load must not pull focus away from wherever they are.
  const submittedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void fetchContactStatus(window.fetch.bind(window)).then((enabled) => {
      if (!cancelled && enabled === false) setPhase('disabled');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (submittedRef.current && (phase === 'sent' || phase === 'disabled')) resultRef.current?.focus();
  }, [phase]);

  const focusField = (field: ContactField | undefined) => {
    const target = field ? formRef.current?.elements.namedItem(field) : null;
    if (target instanceof HTMLElement) target.focus();
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const values: ContactValues = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      organization: String(data.get('organization') ?? ''),
      message: String(data.get('message') ?? ''),
      website: String(data.get('website') ?? ''),
      locale,
    };
    const invalid = validateContact(values);
    if (invalid) {
      setError(invalid);
      focusField(invalid.field);
      return;
    }
    setError(null);
    submittedRef.current = true;
    setPhase('sending');
    const failure = await submitContact(window.fetch.bind(window), values);
    if (!failure) {
      setPhase('sent');
      return;
    }
    if (failure.code === 'not_configured') {
      setPhase('disabled');
      return;
    }
    setPhase('ready');
    setError(failure);
    focusField(failure.field);
  }

  const errorFor = (field: ContactField) => (error?.field === field ? error : null);
  const describedBy = (field: ContactField, hint?: boolean) =>
    [hint ? `${field}-hint` : '', errorFor(field) ? `${field}-error` : ''].filter(Boolean).join(' ') || undefined;
  const fieldError = (field: ContactField) => {
    const fieldErr = errorFor(field);
    if (!fieldErr) return null;
    return (
      <p className="field-error" id={`${field}-error`}>
        <span aria-hidden="true">⚠ </span>
        {t(`contact.errors.${fieldErr.code}`)}
      </p>
    );
  };

  return (
    <div className="container page narrow">
      <h1>{page.headline}</h1>
      <p className="lead">{page.intro}</p>

      {phase === 'sent' ? (
        <section className="card notice notice-success" aria-live="polite">
          <h2 ref={resultRef} tabIndex={-1}>
            {page.success.title}
          </h2>
          <p>{page.success.body}</p>
        </section>
      ) : phase === 'disabled' ? (
        <section className="card notice" aria-live="polite">
          <h2 ref={resultRef} tabIndex={-1}>
            {page.notConfigured.title}
          </h2>
          <p>{page.notConfigured.body}</p>
        </section>
      ) : (
        <form
          ref={formRef}
          className="contact-form card"
          method="post"
          action="/api/contact"
          noValidate
          onSubmit={(event) => void onSubmit(event)}
        >
          {error && !error.field && (
            <div className="form-alert" role="alert">
              <span aria-hidden="true">⚠ </span>
              {t(`contact.errors.${error.code}`)}
            </div>
          )}
          {error?.field && (
            <p className="visually-hidden" role="alert">
              {t('contact.errors.summary')}
            </p>
          )}
          <fieldset>
            <legend className="visually-hidden">{t('contact.form.legend')}</legend>
            <input type="hidden" name="locale" value={locale} />
            {explicitLang && <input type="hidden" name="lang" value={explicitLang} />}
            <div className="field">
              <label htmlFor="name">
                {t('contact.form.name')} <span className="required">({t('contact.form.required')})</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                maxLength={CONTACT_LIMITS.name}
                aria-invalid={errorFor('name') ? true : undefined}
                aria-describedby={describedBy('name')}
              />
              {fieldError('name')}
            </div>
            <div className="field">
              <label htmlFor="email">
                {t('contact.form.email')} <span className="required">({t('contact.form.required')})</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                maxLength={CONTACT_LIMITS.email}
                aria-invalid={errorFor('email') ? true : undefined}
                aria-describedby={describedBy('email', true)}
              />
              <p className="field-hint" id="email-hint">
                {t('contact.form.emailHint')}
              </p>
              {fieldError('email')}
            </div>
            <div className="field">
              <label htmlFor="organization">{t('contact.form.organization')}</label>
              <input
                id="organization"
                name="organization"
                type="text"
                autoComplete="organization"
                maxLength={CONTACT_LIMITS.organization}
                aria-invalid={errorFor('organization') ? true : undefined}
                aria-describedby={describedBy('organization')}
              />
              {fieldError('organization')}
            </div>
            <div className="field">
              <label htmlFor="message">
                {t('contact.form.message')} <span className="required">({t('contact.form.required')})</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={7}
                required
                maxLength={CONTACT_LIMITS.message}
                aria-invalid={errorFor('message') ? true : undefined}
                aria-describedby={describedBy('message', true)}
              />
              <p className="field-hint" id="message-hint">
                {t('contact.form.messageHint')}
              </p>
              {fieldError('message')}
            </div>
            {/* Honeypot: hidden from people and assistive technology. */}
            <div className="hp" aria-hidden="true">
              <label htmlFor="website">{t('contact.form.honeypot')}</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>
          </fieldset>
          <button className="button button-primary" type="submit" disabled={phase === 'sending'}>
            {phase === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
          </button>
          <p className="field-hint">{page.privacyNote}</p>
        </form>
      )}
    </div>
  );
}
